import * as RD from '@radix-ui/react-dropdown-menu'
import { Check, ChevronRight, Circle } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { useMediaQuery } from '@/lib/hooks'
import { cn } from './cn'
import { floatingPanel, menuItem } from './popover-styles'
import { usePrivateAttrs } from './privateArea'

export const DropdownMenu = RD.Root
export const DropdownMenuTrigger = RD.Trigger
export const DropdownMenuGroup = RD.Group
export const DropdownMenuSub = RD.Sub
export const DropdownMenuRadioGroup = RD.RadioGroup

/**
 * Menu panel. It never grows past the room between its trigger and the screen edge (Radix measures it after the
 * flip); a longer menu scrolls inside itself, which the page scroll lock allows, with a soft edge shadow while more
 * is below or above. An owner's post menu on a phone (11 items, 493 px) ran under the screen with Delete out of
 * reach, or opened upward over the top bar (UXA-1).
 */
export function DropdownMenuContent({
  className,
  sideOffset = 6,
  collisionPadding = 8,
  align = 'end',
  ...rest
}: ComponentProps<typeof RD.Content>) {
  const priv = usePrivateAttrs()
  return (
    <RD.Portal>
      <RD.Content
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        align={align}
        className={cn(
          floatingPanel,
          'max-h-(--radix-dropdown-menu-content-available-height) min-w-[200px] max-w-[calc(100vw-16px)] overflow-y-auto scroll-shadow-y p-1.5',
          className,
        )}
        {...priv}
        {...rest}
      />
    </RD.Portal>
  )
}

export interface DropdownMenuItemProps extends ComponentProps<typeof RD.Item> {
  icon?: ReactNode
  /** Red text and icon, for Delete, Leave, Ban. */
  destructive?: boolean
  /** Right aligned hint, for example a keyboard shortcut. */
  shortcut?: ReactNode
}

export function DropdownMenuItem({ icon, destructive, shortcut, className, children, ...rest }: DropdownMenuItemProps) {
  return (
    <RD.Item
      className={cn(
        menuItem,
        destructive && 'text-danger data-[highlighted]:bg-danger-tint [&_svg]:text-danger',
        className,
      )}
      {...rest}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {shortcut && <span className="ml-auto pl-3 text-micro font-medium text-muted">{shortcut}</span>}
    </RD.Item>
  )
}

export function DropdownMenuCheckboxItem({ className, children, ...rest }: ComponentProps<typeof RD.CheckboxItem>) {
  return (
    <RD.CheckboxItem className={cn(menuItem, 'pr-8', className)} {...rest}>
      <span className="min-w-0 flex-1 truncate">{children}</span>
      <RD.ItemIndicator className="absolute right-2.5 inline-flex">
        <Check className="text-primary!" strokeWidth={2.25} />
      </RD.ItemIndicator>
    </RD.CheckboxItem>
  )
}

export function DropdownMenuRadioItem({ className, children, ...rest }: ComponentProps<typeof RD.RadioItem>) {
  return (
    <RD.RadioItem className={cn(menuItem, 'pr-8', className)} {...rest}>
      <span className="min-w-0 flex-1 truncate">{children}</span>
      <RD.ItemIndicator className="absolute right-3 inline-flex">
        <Circle className="size-2! fill-primary text-primary!" />
      </RD.ItemIndicator>
    </RD.RadioItem>
  )
}

export function DropdownMenuLabel({ className, ...rest }: ComponentProps<typeof RD.Label>) {
  return <RD.Label className={cn('px-2.5 pt-2 pb-1 text-micro text-muted', className)} {...rest} />
}

export function DropdownMenuSeparator({ className, ...rest }: ComponentProps<typeof RD.Separator>) {
  return <RD.Separator className={cn('-mx-1.5 my-1.5 h-px bg-line', className)} {...rest} />
}

export interface DropdownMenuSubTriggerProps extends ComponentProps<typeof RD.SubTrigger> {
  icon?: ReactNode
}

export function DropdownMenuSubTrigger({ icon, className, children, ...rest }: DropdownMenuSubTriggerProps) {
  return (
    <RD.SubTrigger className={cn(menuItem, 'data-[state=open]:bg-surface-2', className)} {...rest}>
      {icon}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      <ChevronRight className="ml-auto" />
    </RD.SubTrigger>
  )
}

/** Below sm a submenu opens over its own menu, 16 px in and one row down, instead of beside it (E2E-J10-08). */
const PHONE_SUBMENU = 'ml-[calc(10px_-_var(--radix-popper-anchor-width))] w-[calc(var(--radix-popper-anchor-width)_-_16px)]'

/**
 * Submenu. Beside its menu there is no room on a phone (the avatar menu's Theme choices opened 61 px off the left
 * edge, cut to "ght", "rk", "stem"), so below sm it covers the menu just under the row that opened it.
 */
export function DropdownMenuSubContent({ className, sideOffset = 6, alignOffset = -6, avoidCollisions, ...rest }: ComponentProps<typeof RD.SubContent>) {
  const phone = !useMediaQuery('(min-width: 640px)')
  const priv = usePrivateAttrs()
  return (
    <RD.Portal>
      <RD.SubContent
        sideOffset={sideOffset}
        alignOffset={phone ? 40 : alignOffset}
        avoidCollisions={phone ? false : avoidCollisions}
        className={cn(floatingPanel, 'min-w-[180px] p-1.5', phone && PHONE_SUBMENU, className)}
        {...priv}
        {...rest}
      />
    </RD.Portal>
  )
}
